import { useRequiredContext } from "@redotech/react-util/context";
import {
  ASSIGN_ACTION_UNASSIGN_USER_ID,
  ASSIGN_ACTION_UNASSIGN_USER_NAME,
} from "@redotech/redo-model/advanced-flow/rules/actions";
import type { AssignAction as AssignActionType } from "@redotech/redo-model/advanced-flow/rules/schemas";
import { GetUser } from "@redotech/redo-model/user";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo } from "react";

export const AssignAction = memo(function AssignAction({
  action,
  onChange,
  editableFields,
}: {
  action: AssignActionType;
  onChange: (updated: AssignActionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const userEditable =
    editableFields === undefined || editableFields.has("user");

  const team = useRequiredContext(TeamContext);

  const userOptions: RedoListItem<{ id: string; name: string }>[] =
    useMemo(() => {
      const users = (team?.users || []).map((teamUser) => {
        return {
          id: (teamUser.user as GetUser)._id,
          name: (teamUser.user as GetUser).name,
        };
      });

      const userItems = users.map((user) => ({
        id: user.id,
        type: "text" as const,
        text: user.name,
        value: user,
      }));

      const unassignItem: RedoListItem<{ id: string; name: string }> = {
        id: ASSIGN_ACTION_UNASSIGN_USER_ID,
        type: "text",
        text: ASSIGN_ACTION_UNASSIGN_USER_NAME,
        value: {
          id: ASSIGN_ACTION_UNASSIGN_USER_ID,
          name: ASSIGN_ACTION_UNASSIGN_USER_NAME,
        },
      };

      return [unassignItem, ...userItems];
    }, [team?.users]);

  const selectedUser = useMemo(() => {
    return userOptions.find((opt) => opt.value.id === action.user?.id);
  }, [userOptions, action.user?.id]);

  return (
    <Flex dir="column" gap="sm" w="full">
      <RedoSingleSelectDropdownInput<{ id: string; name: string }>
        disabled={!userEditable}
        fitToAnchor
        label="User"
        options={userOptions}
        optionSelected={({ item }) => {
          if (!userEditable) {
            return;
          }
          onChange({ ...action, user: item.value });
        }}
        selectedItem={selectedUser}
      />
    </Flex>
  );
});
